import { useMutation } from "@apollo/client";
import { useMemo, useState } from "react";
import {
  SendProgramReminderMutationMutation,
  SendProgramReminderMutationMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import CustomTextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { classNames, textareaClassName } from "@helpers/css";

import sendProgramReminderMutation from "../graphql/send-program-reminder-mutation";
import { filterOverdueParticipants } from "./compliance-program-progress-constants";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableKeyName,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";

/**
 * The mutation argument each Progress tab column sends its overdue people in.
 *
 * Calibration and sharing are missing on purpose: they are an admin's own work, not a
 * participant's, so there is nobody to remind. Peer nominations are missing because a
 * nomination has no due date of its own yet, so nothing about it can be late.
 */
const reminderArgumentByColumn: Partial<
  Record<
    ComplianceProgramReportingEntityTableKeyName,
    keyof SendProgramReminderMutationMutationVariables
  >
> = {
  oneononeStatus: "oneononeStatusUserIds",
  performanceAssessmentStatus: "performanceAssessmentStatusUserIds",
  performanceSelfAssessmentStatus: "performanceSelfAssessmentStatusUserIds",
  managerAssessmentStatus: "managerAssessmentStatusUserIds",
  peerAssessmentStatus: "peerAssessmentStatusUserIds",
  engagementSurveyStatus: "engagementSurveyStatusUserIds",
};

/**
 * Send reminders about the overdue work on the rows the admin selected.
 *
 * The rows identify review subjects. The server finds the responsible recipients for
 * each step. For example, it sends a manager-review reminder to the subject's manager.
 */
const ComplianceProgramRemindOverdueDialog = ({
  complianceProgramId,
  cols,
  participantStatuses,
  onClose,
}: {
  complianceProgramId: number;
  cols: ComplianceProgramReportingEntityTableCol[];
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[];
  onClose: () => void;
}) => {
  const currentOrganization = currentOrganizationVar();
  const label = useLabel();
  const [message, setMessage] = useState("");
  const [emailNotification, setEmailNotification] = useState(true);
  const [slackNotification, setSlackNotification] = useState(
    !!currentOrganization.hasSlackConnection,
  );
  const [teamsNotification, setTeamsNotification] = useState(
    !!currentOrganization.hasTeamsNotificationConnection,
  );

  const overdueByColumn = useMemo(() => {
    return cols
      .map((col) => ({
        col,
        argument: reminderArgumentByColumn[col.keyName],
        users: filterOverdueParticipants(participantStatuses, col.keyName).map(
          (participantStatus) => participantStatus.user,
        ),
      }))
      .filter((entry) => entry.argument && entry.users.length > 0);
  }, [cols, participantStatuses]);

  const subjectCount = useMemo(
    () =>
      new Set(
        overdueByColumn.flatMap((entry) => entry.users.map((user) => user.id)),
      ).size,
    [overdueByColumn],
  );

  const [sendReminder, { loading }] = useMutation<
    SendProgramReminderMutationMutation,
    SendProgramReminderMutationMutationVariables
  >(sendProgramReminderMutation);

  const handleClickSend = () => {
    const userIdsByArgument = overdueByColumn.reduce(
      (memo, entry) => ({
        ...memo,
        [entry.argument as string]: entry.users.map((user) => user.id),
      }),
      {} as Record<string, number[]>,
    );
    sendReminder({
      variables: {
        complianceProgramId,
        message: message || null,
        overdueOnly: true,
        emailNotification,
        slackNotification,
        teamsNotification,
        ...userIdsByArgument,
      },
      onCompleted: (data) => {
        successNotificationVar(
          data.sendProgramReminder?.didSend
            ? { title: "Reminder sent" }
            : {
                title: "No reminders sent",
                description: "The selected work is no longer overdue.",
              },
        );
        onClose();
      },
      onError: (error) => {
        // The dialog stays open so the admin can try again without rebuilding it.
        errorNotificationVar({
          title: "Reminder not sent",
          description: error.message,
        });
      },
    });
  };

  const noChannel =
    !emailNotification && !slackNotification && !teamsNotification;

  return (
    <Modal onClose={onClose} alignedTop>
      <div
        className="flex flex-col gap-6 p-6 text-sm"
        aria-label="Remind all overdue dialog"
      >
        <ModalTitle onClose={onClose}>
          Remind people about overdue work
        </ModalTitle>

        <div className="text-gray-600">
          {subjectCount === 0 ? (
            <>Nobody is overdue on the steps that can be reminded.</>
          ) : (
            <>
              This sends reminders about overdue work for{" "}
              <span className="font-medium">{subjectCount}</span>{" "}
              {subjectCount === 1 ? "participant" : "participants"} in this{" "}
              {label("review")}. The person responsible for each step is
              contacted. This can be the participant, their manager, their
              peers, or their reports.
            </>
          )}
        </div>

        {overdueByColumn.length > 0 && (
          <div className="flex flex-col divide-y border-t border-b text-gray-600">
            {overdueByColumn.map((entry) => (
              <div
                key={entry.col.keyName}
                className="flex items-center justify-between gap-2 py-2"
              >
                <div>{entry.col.label}</div>
                <div className="text-gray-400 text-xs">
                  {entry.users.length} overdue
                </div>
              </div>
            ))}
          </div>
        )}

        {(currentOrganization.hasSlackConnection ||
          currentOrganization.hasTeamsNotificationConnection) && (
          <div className="flex gap-2">
            <label className="text-gray-500 w-42 pt-1.5">Reminder Type</label>
            <div className="flex-1 flex gap-6">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailNotification}
                  onChange={(e) => setEmailNotification(e.target.checked)}
                />
                Email
              </label>
              {currentOrganization.hasSlackConnection && (
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={slackNotification}
                    onChange={(e) => setSlackNotification(e.target.checked)}
                  />
                  Slack
                </label>
              )}
              {currentOrganization.hasTeamsNotificationConnection && (
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={teamsNotification}
                    onChange={(e) => setTeamsNotification(e.target.checked)}
                  />
                  Teams
                </label>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <label className="text-gray-500 w-42 pt-1.5">Message</label>
          <div className="flex-1">
            <CustomTextareaAutosize
              className={classNames(textareaClassName, "min-h-20 max-h-36")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2">
          <Button
            aria-label="Confirm remind overdue button"
            text="Send reminder"
            theme={buttonTheme.primary}
            disabled={loading || subjectCount === 0 || noChannel}
            tooltip={
              noChannel ? "Please select at least one reminder type" : undefined
            }
            onClick={handleClickSend}
          />
          <Button text="Cancel" onClick={onClose} />
          {loading && <Loading mini size="5" />}
        </div>
      </div>
    </Modal>
  );
};

export default ComplianceProgramRemindOverdueDialog;
